/**
 * Per-section streaming types (US 20430 / design "Section_Events").
 *
 * The backend emits **additive** Section_Events over the EXISTING
 * `/ws/snap-status/{user_id}` WebSocket as each report section evolves:
 * `started -> report_section -> completed`, and `error` on failure. Legacy
 * clients ignore unknown `message_type`s without breaking.
 *
 * These shapes are consumed defensively: the backend emitters land in tasks
 * 4.5/4.6, so every field except the discriminator and `event` may be missing
 * or malformed at runtime and is validated at the boundary (see
 * `parseSectionEvent`). The status layer never reads or stores report content
 * or PII — only the section id, an optional source label, and the lifecycle
 * status.
 */

/** Per-section UI status tracked by the frontend. */
export type SectionStatusValue = "idle" | "loading" | "ready" | "error";

/** Lifecycle stage carried by a Section_Event. */
export type SectionEventType =
  | "started"
  | "report_section"
  | "completed"
  | "error";

/**
 * Discriminator value on the WebSocket payload that marks a per-section
 * streaming event. Legacy/unknown `message_type`s are ignored by the consumer.
 */
export const SECTION_MESSAGE_TYPE = "section" as const;

/**
 * Typed shape of a Section_Event consumed from the status WebSocket.
 *
 * Only `message_type`, `event` and `section_id` are required for the consumer
 * to act; everything else is optional to stay resilient to partial backend
 * payloads.
 */
export interface SectionEvent {
  /** Discriminator. Always `"section"` for events this layer handles. */
  message_type: typeof SECTION_MESSAGE_TYPE;
  /** Lifecycle stage of the section. */
  event: SectionEventType;
  /** Stable identifier of the section this event refers to. */
  section_id: string;
  /**
   * Human-friendly source label used to build progressive status messages
   * (e.g. "redes sociais", "processos"). Optional: the section -> label
   * mapping is an open question in the design, so a generic fallback is used
   * when it is absent.
   */
  section_label?: string;
  /** Owning report id, present once the pending report exists. */
  user_reports_id?: string;
  /**
   * Section payload, only meaningful for the `report_section` event. Left
   * untyped here: the payload shape is owned by the rendering/persistence
   * layer (tasks 7.3-7.5). The status layer never reads or stores it.
   */
  section?: unknown;
  /**
   * Set only by the "Buscar mais dados" incremental pipeline (task #21217's
   * `section_result_consumer.py`) — absent on the original initial-load
   * streaming preview event. Distinguishes an on-demand per-section result
   * (task #21220 merges `section` into the already-rendered report) from the
   * ephemeral preview (status-only, never merged into rendered content).
   */
  incremental?: boolean;
}

/** Status map entry kept per section by the status store. */
export interface SectionStatusEntry {
  status: SectionStatusValue;
  /** Last known source label, retained across events that omit it. */
  label?: string;
}
